'use client';

import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TablePagination,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';

import AddEmployeeDrawer from './AddEmployeeForm';
import ContractDrawer, { ContractData } from './ContractDrawer';
import EditEmployeeDrawer from './EditEmployeeDrawer';

import { v4 as uuidv4 } from 'uuid';
import axiosClient from '../../api/axiosClient';

interface Employee {
  id: number;
  name: string;
  email: string;
  phone: string;
  companyId: number;
  status: 'ACTIVE' | 'DISABLED' | 'CREATED';
  createdAt: string;
  updatedAt: string;
  contract?: {
    registrationNumber: string;
    status: string;
    id: number;
    [key: string]: any;
  };
  category?: {
    name: string;
    [key: string]: any;
  };
}

interface EmployeeFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position?: string;
  registrationNumber?: string;
  assignedCategoryId?: number;
  onboardingDate?: string;
  startDate?: string;
  endDate?: string;
  remoteWorkSurfaceArea?: number;
}

export default function EmployeesView() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] =
    useState<'CREATED' | 'ACTIVE' | 'DISABLED'>('CREATED');

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null
  );

  const [showContractDrawer, setShowContractDrawer] = useState(false);
  const [editingContract, setEditingContract] = useState<
    { id: number } | ContractData | null
  >(null);

  const [showEditEmployee, setShowEditEmployee] = useState(false);

  // 🟣 END CONTRACT STATE
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [page, rowsPerPage, statusFilter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      const res = await axiosClient.get('/employees', {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          status: statusFilter !== 'CREATED' ? statusFilter : undefined,
        },
      });

      setEmployees(res.data.employees || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Erreur lors du chargement des employés'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (data: Partial<EmployeeFormData>) => {
    const idempotencyKey = `tpa:create-employee:${uuidv4()}`;

    try {
      setLoading(true);

      await axiosClient.post(
        '/employees',
        {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          position: data.position || 'Employee',
          registrationNumber: data.registrationNumber || 'REG-' + Date.now(),
          assignedCategoryId: data.assignedCategoryId || 1,
          onboardingDate: data.onboardingDate || new Date().toISOString(),
          startDate: data.startDate || new Date().toISOString(),
          endDate: data.endDate || new Date().toISOString(),
          remoteWorkSurfaceArea: data.remoteWorkSurfaceArea || 0,
        },
        {
          headers: { 'Idempotency-Key': idempotencyKey },
        }
      );

      await fetchEmployees();
      setShowAddForm(false);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Erreur lors de la création de l’employé'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEndContract = async (employee: Employee) => {
    if (!employee.contract || employee.contract.status !== 'ACTIVE') return;

    try {
      setEnding(true);

      await axiosClient.post(
        `/employees/${employee.id}/contracts/${employee.contract.id}/end-contract`,
        {
          endDate: new Date().toISOString(),
        }
      );

      await fetchEmployees();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur pour terminer le contrat');
    } finally {
      setEnding(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={4}>
      <h1 style={{ color: '#714291', marginBottom: '1rem' }}>
        Employees Management
      </h1>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Stack alignItems="center" sx={{ my: 2 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" mb={4}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="CREATED">Created</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="DISABLED">Disabled</MenuItem>
        </TextField>

        <Button
          variant="contained"
          onClick={() => setShowAddForm(true)}
          sx={{
            backgroundColor: '#714291',
            '&:hover': { backgroundColor: '#5a336f' },
          }}
        >
          Add Employee
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#714291' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Nom</TableCell>
              <TableCell sx={{ color: '#fff' }}>Prénom</TableCell>
              <TableCell sx={{ color: '#fff' }}>Catégorie</TableCell>
              <TableCell sx={{ color: '#fff' }}>Phone</TableCell>
              <TableCell sx={{ color: '#fff' }}>Email</TableCell>
              <TableCell sx={{ color: '#fff' }}>Status Contract</TableCell>
              <TableCell sx={{ color: '#fff', textAlign: 'center' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {employees.map((emp) => {
              const [firstName, ...lastNameArr] = emp.name.split(' ');
              const lastName = lastNameArr.join(' ');

              return (
                <TableRow key={emp.id}>
                  <TableCell>{firstName}</TableCell>
                  <TableCell>{lastName}</TableCell>
                  <TableCell>{emp.category?.name || '-'}</TableCell>
                  <TableCell>{emp.phone}</TableCell>
                  <TableCell>{emp.email}</TableCell>
                  <TableCell>{emp.contract?.status || '-'}</TableCell>

                  <TableCell sx={{ textAlign: 'center' }}>
                    <Stack direction="row" spacing={1} justifyContent="center">

                   
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setSelectedEmployeeId(emp.id);
                          setShowEditEmployee(true);
                        }}
                      >
                        Edit Employee
                      </Button>

                    
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          setSelectedEmployeeId(emp.id);
                          setEditingContract(null);
                          setShowContractDrawer(true);
                        }}
                      >
                        New Contract
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        disabled={
                          ending ||
                          !emp.contract ||
                          emp.contract.status !== 'ACTIVE'
                        }
                        onClick={() => handleEndContract(emp)}
                      >
                        {ending ? 'Ending...' : 'End Contract'}
                      </Button>

                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <AddEmployeeDrawer
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSubmit={handleAddEmployee}
      />

      <ContractDrawer
        open={showContractDrawer}
        onClose={() => setShowContractDrawer(false)}
        employeeId={selectedEmployeeId}
        editingContract={editingContract}
        onSuccess={() => {
          fetchEmployees();
          setShowContractDrawer(false);
        }}
      />

      <EditEmployeeDrawer
        open={showEditEmployee}
        onClose={() => setShowEditEmployee(false)}
        employeeId={selectedEmployeeId}
        onSuccess={fetchEmployees}
      />
    </Box>
  );
}
